import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';
import { Loader2, Plus, X } from 'lucide-react';
import {
  getIntakePatientTags,
  getPatientTagDefinitions,
  replaceIntakePatientTags,
  type PatientTagDefinitionResponse,
  type PatientTagResponse,
} from '../../services/patientTagsApi';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface IntakePatientTagsFieldProps {
  intakeId: string;
  clinicId?: string | null;
  disabled?: boolean;
  onSavingChange?: (saving: boolean) => void;
  onSavePromiseChange?: (promise: Promise<void> | null) => void;
}

/**
 * Intake tags save independently from the demographic/intake autosave. The
 * clinic is the source of truth for the option list; an archived selected tag
 * remains visible and can be removed, but cannot be newly selected.
 */
export const IntakePatientTagsField: React.FC<IntakePatientTagsFieldProps> = ({
  intakeId,
  clinicId,
  disabled = false,
  onSavingChange,
  onSavePromiseChange,
}) => {
  const { showError } = useStore(
    useShallow(state => ({ showError: state.showError }))
  );
  const [definitions, setDefinitions] = useState<
    PatientTagDefinitionResponse[]
  >([]);
  const [selected, setSelected] = useState<PatientTagResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addPopoverOpen, setAddPopoverOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestGeneration = useRef(0);
  const loadedIdentityRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    const identity = `${intakeId}:${clinicId ?? ''}`;
    const generation = ++requestGeneration.current;
    // A save from the previous clinic may still be in flight. It is ignored by
    // the generation guard below, but must not leave the newly selected clinic
    // permanently disabled while it is loading.
    setSaving(false);
    onSavingChange?.(false);
    onSavePromiseChange?.(null);
    setAddPopoverOpen(false);
    loadedIdentityRef.current = null;
    setLoadError(false);
    if (!clinicId) {
      setDefinitions([]);
      setSelected([]);
      setLoading(false);
      loadedIdentityRef.current = identity;
      return;
    }

    setLoading(true);
    try {
      const [nextDefinitions, nextSelected] = await Promise.all([
        getPatientTagDefinitions({ clinicId, includeInactive: true }),
        getIntakePatientTags(intakeId),
      ]);
      if (generation !== requestGeneration.current) return;
      setDefinitions(nextDefinitions);
      setSelected(nextSelected);
      loadedIdentityRef.current = identity;
    } catch (error) {
      console.error('Failed to load intake tags', error);
      if (generation === requestGeneration.current) {
        setLoadError(true);
        showError(
          getBusinessErrorMessage(error) ?? 'Unable to load patient tags.'
        );
      }
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [clinicId, intakeId, onSavePromiseChange, onSavingChange, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveSelection = async (nextIds: string[]) => {
    if (disabled || saving || !clinicId) return;
    const generation = requestGeneration.current;
    setSaving(true);
    onSavingChange?.(true);
    const savePromise = (async () => {
      try {
        const next = await replaceIntakePatientTags(intakeId, {
          clinic_id: clinicId,
          tag_definition_ids: nextIds,
        });
        if (generation === requestGeneration.current) setSelected(next);
      } catch (error) {
        console.error('Failed to save intake tags', error);
        if (generation === requestGeneration.current) {
          showError(
            getBusinessErrorMessage(error) ?? 'Unable to save patient tags.'
          );
        }
        throw error;
      } finally {
        if (generation === requestGeneration.current) {
          setSaving(false);
          onSavingChange?.(false);
          onSavePromiseChange?.(null);
        }
      }
    })();
    onSavePromiseChange?.(savePromise);
    try {
      await savePromise;
    } catch {
      // The error is already shown above. Keep the field available for retry.
    }
  };

  const add = async (definition: PatientTagDefinitionResponse) => {
    if (!clinicId || !definition.is_active) return;
    const selectedIds = new Set(selected.map(tag => tag.id));
    if (selectedIds.has(definition.id)) return;
    selectedIds.add(definition.id);
    await saveSelection([...selectedIds]);
  };

  const remove = async (tag: PatientTagResponse) => {
    await saveSelection(
      selected
        .filter(selectedTag => selectedTag.id !== tag.id)
        .map(tag => tag.id)
    );
  };

  const currentIdentity = `${intakeId}:${clinicId ?? ''}`;
  const waitingForCurrentData =
    Boolean(clinicId) &&
    !loadError &&
    loadedIdentityRef.current !== currentIdentity;
  const activeDefinitionsNotSelected = definitions.filter(
    definition =>
      definition.clinic_id === clinicId &&
      definition.is_active &&
      !selected.some(tag => tag.id === definition.id)
  );
  const hasAvailableTags = activeDefinitionsNotSelected.length > 0;
  const hasConfiguredTags = definitions.some(
    definition => definition.clinic_id === clinicId && definition.is_active
  );

  return (
    <div className='space-y-2 rounded-md border p-3'>
      <div>
        <h3 className='text-sm font-semibold'>Patient tags</h3>
        <p className='text-xs text-muted-foreground'>
          Tags are specific to the selected clinic.
        </p>
      </div>
      {!clinicId ? (
        <p className='text-sm text-muted-foreground'>Select a clinic first.</p>
      ) : loading || waitingForCurrentData ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' /> Loading tags...
        </div>
      ) : loadError ? (
        <div className='space-y-2' role='alert'>
          <p className='text-sm text-destructive'>
            Unable to load patient tags. Please try again.
          </p>
          <Button size='sm' variant='outline' onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : !hasConfiguredTags && selected.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No tags are configured for this clinic.
        </p>
      ) : (
        <div className='flex flex-wrap items-center gap-2'>
          {selected.map(tag => (
            <span
              key={tag.id}
              className='inline-flex max-w-full items-center gap-1.5 rounded-full border bg-muted/40 px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-muted/60'
            >
              <span className='max-w-[12rem] truncate' title={tag.name}>
                {tag.name}
              </span>
              {!tag.is_active && <Badge variant='secondary'>Archived</Badge>}
              <button
                type='button'
                className='inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground'
                aria-label={`Remove ${tag.name}`}
                disabled={disabled || saving}
                onClick={() => void remove(tag)}
              >
                <X className='h-3 w-3' aria-hidden='true' />
              </button>
            </span>
          ))}
          {hasAvailableTags && (
            <Popover open={addPopoverOpen} onOpenChange={setAddPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={disabled || saving}
                  className='h-7 rounded-full border-dashed px-2.5 text-xs text-muted-foreground hover:text-foreground'
                >
                  <Plus className='mr-1 h-3 w-3' aria-hidden='true' />
                  {selected.length === 0 ? 'Add tag' : 'Add'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-64 p-0' align='start'>
                <Command>
                  <CommandInput placeholder='Search tags...' />
                  <CommandList>
                    <CommandEmpty>No tags found.</CommandEmpty>
                    <CommandGroup>
                      {activeDefinitionsNotSelected.map(definition => (
                        <CommandItem
                          key={definition.id}
                          value={definition.name}
                          onSelect={() => {
                            setAddPopoverOpen(false);
                            void add(definition);
                          }}
                        >
                          <span className='truncate'>{definition.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}
    </div>
  );
};
